import "./StaffShifts.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserTie, faMoon, faSun, faCloudSun,
  faRightToBracket, faRightFromBracket, faTriangleExclamation,
  faClock, faUsers, faBolt,
} from "@fortawesome/free-solid-svg-icons";

interface AttendanceRecord {
  id: number;
  staff_name: string;
  staff_id: string;
  role: string;
  clock_in: string | null;
  clock_out: string | null;
  shift: string;
  late_minutes: number;
  overtime_minutes: number;
}

interface ShiftSummary {
  count: number;
  late: number;
  onShift: number;
  checkedOut: number;
  totalOvertime: number;
  records: AttendanceRecord[];
}

interface TodaySummary {
  date: string;
  morning: ShiftSummary;
  afternoon: ShiftSummary;
  night: ShiftSummary;
  total: number;
}

interface Props {
  summary: TodaySummary | null;
  loading?: boolean;
}

const SHIFT_META = {
  morning:   { label: "Morning",   time: "08:00 – 14:00", icon: faSun,      color: "#f59e0b", bg: "rgba(245,158,11,0.1)" },
  afternoon: { label: "Afternoon", time: "14:00 – 22:00", icon: faCloudSun, color: "#6366f1", bg: "rgba(99,102,241,0.1)" },
  night:     { label: "Night",     time: "22:00 – 08:00", icon: faMoon,     color: "#8b5cf6", bg: "rgba(139,92,246,0.1)" },
};

const ROLE_COLORS: Record<string, string> = {
  receptionist: "#34d399",
  housekeeping: "#fbbf24",
  restaurant:   "#a78bfa",
  security:     "#f87171",
  maintenance:  "#fb923c",
  other:        "#64748b",
};

function fmt(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

function activeShift(): "morning" | "afternoon" | "night" {
  const h = new Date().getHours();
  if (h >= 8 && h < 14) return "morning";
  if (h >= 14 && h < 22) return "afternoon";
  return "night";
}

export default function StaffShifts({ summary, loading }: Props) {
  const current = activeShift();

  if (loading) {
    return (
      <div className="ss-root">
        <div className="ss-header">
          <div className="ss-header-left">
            <div className="ss-shift-icon"><FontAwesomeIcon icon={faUsers} /></div>
            <div>
              <h3 className="ss-title">Staff On Duty</h3>
              <p className="ss-shift-name">Loading…</p>
            </div>
          </div>
        </div>
        <div className="ss-empty"><FontAwesomeIcon icon={faUserTie} className="ss-empty-icon" /><p>Loading attendance…</p></div>
      </div>
    );
  }

  if (!summary || summary.total === 0) {
    return (
      <div className="ss-root">
        <div className="ss-header">
          <div className="ss-header-left">
            <div className="ss-shift-icon"><FontAwesomeIcon icon={faUsers} /></div>
            <div>
              <h3 className="ss-title">Staff On Duty</h3>
              <p className="ss-shift-name">Today's attendance</p>
            </div>
          </div>
          <span className="ss-count">0 clocked in</span>
        </div>
        <div className="ss-empty">
          <FontAwesomeIcon icon={faUserTie} className="ss-empty-icon" />
          <p>No staff clocked in today</p>
        </div>
      </div>
    );
  }

  const shifts = (["morning", "afternoon", "night"] as const).map(key => ({
    key,
    meta: SHIFT_META[key],
    data: summary[key],
    isCurrent: key === current,
  }));

  const onShiftNow = (summary.morning.onShift || 0) + (summary.afternoon.onShift || 0) + (summary.night.onShift || 0);
  const lateCount  = (summary.morning.late || 0) + (summary.afternoon.late || 0) + (summary.night.late || 0);

  return (
    <div className="ss-root">
      {/* Header */}
      <div className="ss-header">
        <div className="ss-header-left">
          <div className="ss-shift-icon">
            <FontAwesomeIcon icon={SHIFT_META[current].icon} />
          </div>
          <div>
            <h3 className="ss-title">Staff On Duty</h3>
            <p className="ss-shift-name">
              {SHIFT_META[current].label} Shift · {SHIFT_META[current].time}
            </p>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {lateCount > 0 && (
            <span className="ss-late-badge">
              <FontAwesomeIcon icon={faTriangleExclamation} style={{ marginRight: 4 }} />
              {lateCount} late
            </span>
          )}
          <span className="ss-count">{onShiftNow} on shift</span>
        </div>
      </div>

      {/* Shift columns */}
      <div className="ss-shifts-row">
        {shifts.map(({ key, meta, data, isCurrent }) => (
          <div key={key} className={`ss-shift-col${isCurrent ? " current" : ""}`}>
            {/* Shift header */}
            <div className="ss-shift-head" style={{ borderColor: meta.color + "33", background: meta.bg }}>
              <FontAwesomeIcon icon={meta.icon} style={{ color: meta.color, marginRight: 6 }} />
              <span style={{ color: meta.color, fontWeight: 700, fontSize: 12 }}>{meta.label}</span>
              <span style={{ color: "rgba(242,235,217,0.45)", fontSize: 11, marginLeft: 4 }}>{meta.time}</span>
              {isCurrent && <span className="ss-current-tag">Current</span>}
              <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
                <span className="ss-shift-stat">
                  <FontAwesomeIcon icon={faClock} style={{ color: meta.color }} />
                  {data.onShift}
                </span>
                <span className="ss-shift-stat">
                  <FontAwesomeIcon icon={faRightFromBracket} style={{ color: "rgba(242,235,217,0.45)" }} />
                  {data.checkedOut}
                </span>
                {data.late > 0 && (
                  <span className="ss-shift-stat" style={{ color: "#f87171" }}>
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    {data.late}
                  </span>
                )}
              </div>
            </div>

            {/* Staff rows */}
            {data.records.length === 0 ? (
              <div className="ss-shift-empty">No staff this shift</div>
            ) : (
              data.records.map(r => (
                <div key={r.id} className={`ss-staff-row${!r.clock_out ? " active" : ""}`}>
                  <div className="ss-staff-avatar" style={{ background: (ROLE_COLORS[r.role] ?? "#64748b") + "22" }}>
                    <span style={{ color: ROLE_COLORS[r.role] ?? "#64748b", fontSize: 12, fontWeight: 800 }}>
                      {r.staff_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="ss-staff-info">
                    <span className="ss-staff-name">{r.staff_name}</span>
                    <span className="ss-staff-role" style={{ color: ROLE_COLORS[r.role] ?? "#64748b" }}>
                      {r.role}
                    </span>
                  </div>
                  <div className="ss-staff-times">
                    <span className="ss-time-in">
                      <FontAwesomeIcon icon={faRightToBracket} style={{ color: "#34d399" }} />
                      {fmt(r.clock_in)}
                    </span>
                    {r.clock_out ? (
                      <span className="ss-time-out">
                        <FontAwesomeIcon icon={faRightFromBracket} style={{ color: "#f87171" }} />
                        {fmt(r.clock_out)}
                      </span>
                    ) : (
                      <span className="ss-on-shift-badge">On Shift</span>
                    )}
                  </div>
                  <div className="ss-staff-flags">
                    {r.late_minutes > 0 && (
                      <span className="ss-flag-late" title={`${r.late_minutes}m late`}>
                        <FontAwesomeIcon icon={faTriangleExclamation} /> {r.late_minutes}m
                      </span>
                    )}
                    {r.overtime_minutes > 0 && (
                      <span className="ss-flag-ot" title={`${r.overtime_minutes}m overtime`}>
                        <FontAwesomeIcon icon={faBolt} /> {r.overtime_minutes}m
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
